import { useState, useEffect, useRef } from 'react';
import { Handshake, Plus, Trash2, Upload, ExternalLink, Eye, EyeOff, Pencil, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { getAllSponsors, createSponsor, updateSponsor, deleteSponsor, uploadSponsorLogo } from '../../lib/api';
import { toast } from 'sonner';

const SponsorRow = ({ sponsor, onUploadLogo, onToggleActive, onDelete, onEdit }) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      onUploadLogo(sponsor.id, file);
    } else {
      toast.error('Trascina un file immagine valido');
    }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`bg-slate-900 border rounded-xl p-4 flex items-center gap-4 transition-all ${
        dragOver ? 'border-primary border-dashed bg-primary/5 scale-[1.01]' : 'border-slate-800'
      } ${!sponsor.is_active ? 'opacity-50' : ''}`}
      data-testid={`sponsor-row-${sponsor.id}`}
    >
      {/* Logo / dropzone */}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="w-16 h-16 rounded-lg bg-slate-800 flex items-center justify-center overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-primary/50 transition-all"
        title="Carica o trascina un'immagine"
        data-testid={`sponsor-logo-btn-${sponsor.id}`}
      >
        {sponsor.logo_data ? (
          <img
            src={`data:${sponsor.logo_content_type};base64,${sponsor.logo_data}`}
            alt={sponsor.name}
            className="max-w-full max-h-full object-contain"
          />
        ) : dragOver ? (
          <ImageIcon className="w-6 h-6 text-primary animate-pulse" />
        ) : (
          <Handshake className="w-6 h-6 text-slate-600" />
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.[0]) onUploadLogo(sponsor.id, e.target.files[0]);
            e.target.value = '';
          }}
        />
      </button>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold truncate">{sponsor.name}</div>
        {sponsor.website_url ? (
          <a
            href={sponsor.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-400 hover:text-primary flex items-center gap-1 truncate"
          >
            <span className="truncate">{sponsor.website_url}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
        ) : (
          <span className="text-xs text-slate-500 italic">Nessun sito web</span>
        )}
        {dragOver && (
          <div className="text-xs text-primary mt-1">Rilascia per caricare il logo</div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => onEdit(sponsor)}
          className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-primary"
          title="Modifica"
          data-testid={`edit-sponsor-${sponsor.id}`}
        >
          <Pencil className="w-4 h-4" />
        </button>

        <button
          onClick={() => inputRef.current?.click()}
          className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
          title="Carica logo"
          data-testid={`upload-logo-${sponsor.id}`}
        >
          <Upload className="w-4 h-4" />
        </button>

        <button
          onClick={() => onToggleActive(sponsor)}
          className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
          title={sponsor.is_active ? 'Nascondi' : 'Mostra'}
          data-testid={`toggle-sponsor-${sponsor.id}`}
        >
          {sponsor.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>

        <button
          onClick={() => onDelete(sponsor.id, sponsor.name)}
          className="p-2 rounded-lg hover:bg-red-500/10 transition-colors text-slate-400 hover:text-red-400"
          data-testid={`delete-sponsor-${sponsor.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const EditSponsorDialog = ({ sponsor, open, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (sponsor) {
      setName(sponsor.name || '');
      setWebsiteUrl(sponsor.website_url || '');
    }
  }, [sponsor]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Il nome è obbligatorio');
      return;
    }
    setSaving(true);
    try {
      await onSave({ name: name.trim(), website_url: websiteUrl.trim() });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-800" data-testid="edit-sponsor-dialog">
        <DialogHeader>
          <DialogTitle>Modifica sponsor</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Nome</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-slate-800 border-slate-700"
              data-testid="edit-sponsor-name-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Sito web (opzionale)</label>
            <Input
              placeholder="https://..."
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="bg-slate-800 border-slate-700"
              data-testid="edit-sponsor-url-input"
            />
            <p className="text-xs text-slate-500 mt-1">Puoi aggiungerlo anche in un secondo momento.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary text-slate-950 hover:bg-primary/90"
            data-testid="save-edit-sponsor-btn"
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdminSponsorPage = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', website_url: '' });
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [globalDragOver, setGlobalDragOver] = useState(false);
  const newLogoInputRef = useRef(null);

  const fetchSponsors = async () => {
    try {
      const res = await getAllSponsors();
      const sorted = [...res.data].sort((a, b) => a.name.localeCompare(b.name, 'it'));
      setSponsors(sorted);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSponsors(); }, []);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Inserisci il nome dello sponsor');
      return;
    }
    try {
      await createSponsor({ name: formData.name.trim(), website_url: formData.website_url.trim() });
      toast.success('Sponsor aggiunto');
      setFormData({ name: '', website_url: '' });
      setShowForm(false);
      fetchSponsors();
    } catch (error) {
      toast.error('Errore nella creazione');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare lo sponsor "${name}"?`)) return;
    try {
      await deleteSponsor(id);
      toast.success('Sponsor eliminato');
      fetchSponsors();
    } catch (error) {
      toast.error('Errore nella cancellazione');
    }
  };

  const handleToggleActive = async (sponsor) => {
    try {
      await updateSponsor(sponsor.id, { is_active: !sponsor.is_active });
      toast.success(sponsor.is_active ? 'Sponsor nascosto' : 'Sponsor visibile');
      fetchSponsors();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleLogoUpload = async (sponsorId, file) => {
    try {
      await uploadSponsorLogo(sponsorId, file);
      toast.success('Logo caricato');
      fetchSponsors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    }
  };

  const handleEditSave = async (data) => {
    try {
      await updateSponsor(editingSponsor.id, data);
      toast.success('Sponsor aggiornato');
      fetchSponsors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
      throw error;
    }
  };

  // Create new sponsor by dropping image: creates with placeholder name, then uploads logo
  const handleGlobalDrop = async (e) => {
    e.preventDefault();
    setGlobalDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Trascina un file immagine valido');
      return;
    }
    const baseName = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim() || 'Nuovo sponsor';
    try {
      const res = await createSponsor({ name: baseName, website_url: '' });
      const newId = res.data.id;
      await uploadSponsorLogo(newId, file);
      toast.success(`Sponsor "${baseName}" creato con logo`);
      fetchSponsors();
    } catch (error) {
      toast.error('Errore nella creazione');
    }
  };

  return (
    <div
      className="p-4 sm:p-6 lg:p-8 min-h-screen"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setGlobalDragOver(true);
        }
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setGlobalDragOver(false);
      }}
      onDrop={handleGlobalDrop}
      data-testid="admin-sponsor-page"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-3">
              <Handshake className="w-7 h-7 text-primary" />
              Gestione Sponsor
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {sponsors.length} sponsor in ordine alfabetico · Trascina un'immagine per aggiungere
            </p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-primary text-slate-950 hover:bg-primary/90"
            data-testid="add-sponsor-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi
          </Button>
        </div>

        {/* Global drag overlay hint */}
        {globalDragOver && (
          <div className="mb-4 p-6 border-2 border-dashed border-primary rounded-xl bg-primary/5 text-center" data-testid="global-drop-hint">
            <ImageIcon className="w-10 h-10 text-primary mx-auto mb-2" />
            <p className="font-semibold text-primary">Rilascia l'immagine per creare uno sponsor</p>
            <p className="text-xs text-slate-400 mt-1">Userà il nome del file. Potrai modificarlo dopo.</p>
          </div>
        )}

        {/* Add Form */}
        {showForm && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6 mb-6" data-testid="sponsor-form">
            <h3 className="font-heading font-bold mb-4">Nuovo Sponsor</h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <Input
                placeholder="Nome sponsor *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-slate-800 border-slate-700"
                data-testid="sponsor-name-input"
              />
              <Input
                placeholder="URL sito web (opzionale)"
                value={formData.website_url}
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                className="bg-slate-800 border-slate-700"
                data-testid="sponsor-url-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreate} className="bg-primary text-slate-950" data-testid="save-sponsor-btn">
                Salva
              </Button>
              <Button variant="ghost" onClick={() => { setShowForm(false); setFormData({ name: '', website_url: '' }); }}>
                Annulla
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Suggerimento: puoi anche trascinare direttamente un'immagine in questa pagina per creare uno sponsor.
            </p>
          </div>
        )}

        {/* Sponsors List */}
        <div className="space-y-3">
          {sponsors.map((sponsor) => (
            <SponsorRow
              key={sponsor.id}
              sponsor={sponsor}
              onUploadLogo={handleLogoUpload}
              onToggleActive={handleToggleActive}
              onDelete={handleDelete}
              onEdit={(s) => setEditingSponsor(s)}
            />
          ))}

          {sponsors.length === 0 && !loading && (
            <div
              className="text-center py-16 border-2 border-dashed border-slate-700 rounded-xl text-slate-400"
              data-testid="empty-sponsors"
            >
              <ImageIcon className="w-12 h-12 mx-auto mb-3 text-slate-600" />
              <p className="font-semibold mb-1">Nessuno sponsor ancora</p>
              <p className="text-sm">Clicca "Aggiungi" oppure trascina qui un'immagine.</p>
            </div>
          )}
        </div>
      </div>

      <EditSponsorDialog
        sponsor={editingSponsor}
        open={!!editingSponsor}
        onClose={() => setEditingSponsor(null)}
        onSave={handleEditSave}
      />
      {/* hidden input safety */}
      <input ref={newLogoInputRef} type="file" className="hidden" />
    </div>
  );
};

export default AdminSponsorPage;
